import { RunPromptResult } from "./aiCommon";
// eslint-disable-next-line import/no-named-as-default
import runPromptWithProvider from "./llmClient";
import { ParsedPdf } from "./parseZipPdfs";
import { references, Reference } from "./reference";

// Estimate cost in USD using a simple pricing table (per 1M tokens)
const PRICING_USD_PER_MTOK: Record<string, { input: number; output: number }> =
  {
    "openrouter/andromeda-alpha": { input: 0, output: 0 },
    "google/gemini-2.5-pro": { input: 1.25, output: 10 },
    "google/gemini-2.5-flash": { input: 0.3, output: 2.5 },
    "openai/o3-deep-research": { input: 10, output: 40 },
    "openai/gpt-5-pro": { input: 15, output: 120 },
    "gpt-5-nano": { input: 0.05, output: 0.4 },
    "gpt-5-mini": { input: 0.25, output: 2 },
    "openai/gpt-5-mini": { input: 0.25, output: 2 },
    "gpt-5": { input: 1.25, output: 10 },
    "openai/gpt-5.2": { input: 1.75, output: 14 },
    "gpt-5.2": { input: 1.75, output: 14 },
    "gpt-4o": { input: 5, output: 15 },
    "gpt-4o-mini": { input: 0.15, output: 0.6 },
    "gpt-4-turbo": { input: 10, output: 30 },
    "gpt-3.5-turbo": { input: 0.5, output: 1.5 },
  };

// const SHOULD_RUN_PROMPTS = false;
const SHOULD_RUN_PROMPTS = true;
const DEBUG = false;

export const runPromptsForFiles = async (
  files: ParsedPdf[],
  onProgress?: (completed: number, total: number, fileName?: string) => void,
  options: { authToken?: string } = {}
) => {
  const targets = files.filter(
    (file) =>
      file.ectdKey &&
      (file.ectdKey.includes("m3-2-p") ||
        file.ectdKey.includes("m3-2-r") ||
        file.ectdKey.includes("m3-2-s") ||
        file.ectdTitle.includes("common-spc") ||
        file.ectdTitle.includes("common-pl") ||
        file.ectdKey.includes("m1-8-1-pharmacovigilance-system") ||
        file.ectdKey.includes("m2-2-introduction") ||
        file.ectdKey.includes("m2-3-quality-overall-summary") ||
        file.ectdKey.includes("m2-4-nonclinical-overview") ||
        file.ectdKey.includes("m2-5-clinical-overview") ||
        file.ectdKey.includes("m2-6-clinical-summary"))
  );
  // .filter(
  //     (file) => file.fileName.includes('32p')
  // )
  // .filter(
  //     (file) => file.ectdTitle.includes('common-pl')
  // )
  // .slice(0, 1);

  const total = targets.length;
  let completed = 0;

  if (typeof onProgress === "function") {
    onProgress(0, total);
  }

  // console.log("targets: ", targets);

  const tasks: Promise<RunPromptResult>[] = [];

  targets.forEach((file) => {
    let reference: Reference | undefined;

    if (file.ectdKey === "pi-doc") {
      const foundReference = references.find(
        (r) => r.ectdTitle === file.ectdTitle
      );
      if (foundReference) {
        reference = foundReference;
      }
    } else {
      const foundReference = references.find((r) =>
        file.ectdKey.startsWith(r.ectdKey)
      );
      if (foundReference) {
        reference = foundReference;
      }
    }

    // console.log("file: ", file);
    // console.log("reference, file: ", reference, file);

    if (reference) {
      tasks.push(
        runPromptWithProvider(file, reference, {
          provider: "openrouter",
          authToken: options.authToken,
        }).then((res) => {
          completed += 1;
          if (typeof onProgress === "function")
            onProgress(completed, total, file.fileName);
          return res;
        })
      );
    }
  });

  const results = SHOULD_RUN_PROMPTS ? await Promise.all(tasks) : [];
  // console.log(results);

  // Aggregate token usage across results
  const totals = results.reduce(
    (acc, r: any) => {
      const usage = r?.usage || {};
      const prompt = Number(usage.prompt_tokens) || 0;
      const completion = Number(usage.completion_tokens) || 0;
      const tokenTotal = Number(usage.total_tokens) || prompt + completion;
      acc.prompt_tokens += prompt;
      acc.completion_tokens += completion;
      acc.total_tokens += tokenTotal;
      return acc;
    },
    { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
  );

  const resolvePricing = (model: string | undefined) => {
    if (!model) return PRICING_USD_PER_MTOK["gpt-5"];
    const entry = Object.entries(PRICING_USD_PER_MTOK).find(
      ([key]) => model === key || model.startsWith(key)
    );
    if (entry) return entry[1];
    console.warn(
      "Unknown model in resolvePricing, returning pricing for gpt-5",
      model
    );
    return PRICING_USD_PER_MTOK["gpt-5"]; // default fallback
  };

  const costTotals = results.reduce(
    (acc, r: any) => {
      const usage = r?.usage || {};
      const model = r?.model as string | undefined;
      const pricing = resolvePricing(model);
      const prompt = Number(usage.prompt_tokens) || 0;
      const completion = Number(usage.completion_tokens) || 0;
      const inputCost = (prompt / 1_000_000) * pricing.input;
      const outputCost = (completion / 1_000_000) * pricing.output;
      acc.input_usd += inputCost;
      acc.output_usd += outputCost;
      acc.total_usd += inputCost + outputCost;
      return acc;
    },
    { input_usd: 0, output_usd: 0, total_usd: 0 }
  );

  if (DEBUG) {
    console.log("Token usage totals:", totals);
    console.log("Estimated cost (USD):", {
      input_usd: Number(costTotals.input_usd.toFixed(6)),
      output_usd: Number(costTotals.output_usd.toFixed(6)),
      total_usd: Number(costTotals.total_usd.toFixed(6)),
    });
  }

  return results;
};
